import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { checkInService } from '../services/checkInService';
import { geolocationService } from '../services/geolocationService';
import { faceVerificationService } from '../services/faceVerificationService';

export const CheckInComponent = () => {
  const { currentUser, userProfile } = useAuth();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null); // { type: 'success'|'error'|'info', text: string }
  const [ubicacion, setUbicacion] = useState(null);
  const [rostroVerificado, setRostroVerificado] = useState(false);
  const [tipoChecada, setTipoChecada] = useState('entrada');
  const [stream, setStream] = useState(null);
  const [modelsLoaded, setModelsLoaded] = useState(false);
  const [hasFaceRegistered, setHasFaceRegistered] = useState(false);
  const [showRegisterFace, setShowRegisterFace] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());

  // Reloj en tiempo real
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!currentUser) return;

    const init = async () => {
      await loadModels();
      await checkGeolocation();
      await checkFaceRegistration();
    };
    init();

    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [currentUser, userProfile?.sucursalId]);

  const loadModels = async () => {
    try {
      await faceVerificationService.loadModels();
      setModelsLoaded(true);
    } catch (error) {
      console.error('Error cargando modelos:', error);
    }
  };

  const checkFaceRegistration = async () => {
    const hasFace = await faceVerificationService.hasFaceRegistered(currentUser.uid);
    setHasFaceRegistered(hasFace);
    if (!hasFace) {
      setShowRegisterFace(true);
    }
  };

  const checkGeolocation = async () => {
    const geoResult = await geolocationService.isWithinGeofence(userProfile?.sucursalId);
    setUbicacion(geoResult);
  };

  const startCamera = async () => {
    try {
      setMessage({ type: 'info', text: 'Iniciando cámara...' });
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'user',
          width: { ideal: 640 },
          height: { ideal: 480 }
        },
        audio: false
      });
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        await videoRef.current.play();
        setStream(mediaStream);
        setMessage({ type: 'info', text: 'Cámara activa. Mira de frente para verificar tu rostro.' });
      }
    } catch (error) {
      console.error('Error al acceder a la cámara:', error);
      setMessage({ type: 'error', text: 'Por favor concede los permisos de cámara en tu celular.' });
    }
  };

  const captureAndVerify = async () => {
    if (!videoRef.current || !stream) {
      await startCamera();
      return;
    }

    const video = videoRef.current;
    if (video.readyState < 2 || video.videoWidth === 0 || video.videoHeight === 0) {
      setMessage({ type: 'info', text: 'Preparando imagen... Intenta de nuevo.' });
      return;
    }

    const canvas = canvasRef.current;
    if (canvas) {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    }

    const targetElement = canvas || video;

    setLoading(true);
    setMessage({ type: 'info', text: 'Escaneando rostro...' });
    const result = await faceVerificationService.verifyFace(currentUser.uid, targetElement);
    if (result.success && result.verified) {
      setRostroVerificado(true);
      setMessage({ type: 'success', text: `✨ ¡Rostro reconocido! (${result.confidence}% coincidencia). Ya puedes registrar asistencia.` });
    } else {
      setRostroVerificado(false);
      setMessage({ type: 'error', text: result.error || 'No se reconoció el rostro. Intenta de nuevo mirando de frente.' });
    }
    setLoading(false);
  };

  const registerFace = async () => {
    if (!videoRef.current || !stream) {
      await startCamera();
      return;
    }

    const video = videoRef.current;
    if (video.readyState < 2 || video.videoWidth === 0 || video.videoHeight === 0) {
      setMessage({ type: 'info', text: 'Preparando cámara... Intenta de nuevo.' });
      return;
    }

    const canvas = canvasRef.current;
    if (canvas) {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    }

    const targetElement = canvas || video;

    setLoading(true);
    setMessage({ type: 'info', text: 'Guardando datos biométricos...' });
    const result = await faceVerificationService.registerFace(currentUser.uid, targetElement);
    if (result.success) {
      setHasFaceRegistered(true);
      setShowRegisterFace(false);
      setRostroVerificado(true);
      setMessage({ type: 'success', text: '🎉 ¡Rostro registrado con éxito! Ya puedes registrar tu asistencia.' });
    } else {
      setMessage({ type: 'error', text: result.error || 'No se pudo registrar el rostro. Intenta de nuevo.' });
    }
    setLoading(false);
  };

  const handleCheckIn = async () => {
    if (!hasFaceRegistered) {
      setShowRegisterFace(true);
      setMessage({ type: 'error', text: 'Primero registra tu rostro para continuar.' });
      return;
    }
    if (!rostroVerificado) {
      setMessage({ type: 'error', text: 'Por favor verifica tu rostro primero.' });
      return;
    }

    setLoading(true);
    const locationData = ubicacion ? {
      latitude: ubicacion.position?.latitude ?? null,
      longitude: ubicacion.position?.longitude ?? null,
      distance: ubicacion.distance ?? null,
      withinGeofence: Boolean(ubicacion.withinGeofence),
      sucursalNombre: ubicacion.sucursalNombre || 'Sucursal'
    } : null;

    const result = await checkInService.checkIn(
      currentUser.uid,
      tipoChecada,
      locationData
    );

    if (result.success) {
      const tipoNombres = {
        entrada: 'Entrada',
        salida_comida: 'Salida a Comer',
        entrada_comida: 'Regreso de Comer',
        salida: 'Salida'
      };
      setMessage({ 
        type: 'success', 
        text: `✅ ${tipoNombres[tipoChecada] || 'Asistencia'} registrada correctamente. ¡Que tengas un excelente día!` 
      });
      setRostroVerificado(false);
    } else {
      setMessage({ type: 'error', text: `Error al registrar: ${result.error}` });
    }
    setLoading(false);
  };

  const options = [
    { id: 'entrada', label: 'Entrada', icon: 'fa-right-to-bracket', color: '#10b981' },
    { id: 'salida_comida', label: 'Salida Comida', icon: 'fa-utensils', color: '#f59e0b' },
    { id: 'entrada_comida', label: 'Regreso Comida', icon: 'fa-rotate-left', color: '#3b82f6' },
    { id: 'salida', label: 'Salida', icon: 'fa-door-open', color: '#ef4444' }
  ];

  const displayName = userProfile?.nombre || currentUser?.displayName || currentUser?.email?.split('@')[0] || 'Colaborador';

  const formattedTime = currentTime.toLocaleTimeString('es-MX', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true
  });

  const formattedDate = currentTime.toLocaleDateString('es-MX', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });

  return (
    <div className="mobile-app-container">
      {/* Header Card con Saludo y Reloj */}
      <div className="mobile-header-card">
        <div className="greeting-row">
          <div>
            <span className="greeting-sub">Hola 👋</span>
            <h2 className="greeting-name">{displayName}</h2>
          </div>
          <div className="status-chip-gps">
            <span className="pulse-dot"></span>
            <span>GPS Activo</span>
          </div>
        </div>

        <div className="clock-section">
          <div className="clock-digits">{formattedTime}</div>
          <div className="clock-date">{formattedDate}</div>
        </div>
      </div>

      {/* Tarjeta Principal de Checada */}
      <div className="mobile-checkin-card">
        {/* Viewfinder de la Cámara */}
        <div className="viewfinder-wrapper">
          <div className={`camera-viewfinder ${rostroVerificado ? 'verified' : stream ? 'active' : ''}`}>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="viewfinder-video"
            />
            <canvas ref={canvasRef} style={{ display: 'none' }} />

            {/* Guías de escaneo facial */}
            <div className="scan-overlay">
              <div className="scan-corner tl"></div>
              <div className="scan-corner tr"></div>
              <div className="scan-corner bl"></div>
              <div className="scan-corner br"></div>
              {stream && !rostroVerificado && <div className="scan-laser"></div>}
            </div>

            {/* Badge de estado flotante */}
            <div className="viewfinder-badge">
              {rostroVerificado ? (
                <span className="badge-pill verified">
                  <i className="fa-solid fa-circle-check"></i> Rostro Verificado
                </span>
              ) : stream ? (
                <span className="badge-pill live">
                  <i className="fa-solid fa-camera"></i> Cámara Activa
                </span>
              ) : (
                <span className="badge-pill standby">
                  <i className="fa-solid fa-shield-halved"></i> Reconocimiento Facial
                </span>
              )}
            </div>

            {!stream && (
              <div className="camera-placeholder" onClick={startCamera}>
                <div className="placeholder-icon">
                  <i className="fa-solid fa-camera"></i>
                </div>
                <p>Toca para activar la cámara</p>
              </div>
            )}
          </div>
        </div>

        {/* Acciones de Cámara y Biometría */}
        <div className="biometric-actions">
          {showRegisterFace && !hasFaceRegistered ? (
            <div className="action-row-full">
              {!stream ? (
                <button onClick={startCamera} className="btn-mobile btn-camera">
                  <i className="fa-solid fa-camera"></i> Activar Cámara
                </button>
              ) : (
                <button onClick={registerFace} disabled={loading} className="btn-mobile btn-primary-action">
                  {loading ? <i className="fa-solid fa-circle-notch fa-spin"></i> : <i className="fa-solid fa-user-plus"></i>}
                  <span>Registrar Mi Rostro</span>
                </button>
              )}
            </div>
          ) : (
            <div className="action-row-full">
              {!stream ? (
                <button onClick={startCamera} className="btn-mobile btn-camera" disabled={!modelsLoaded}>
                  <i className="fa-solid fa-camera"></i> Activar Cámara
                </button>
              ) : (
                <button 
                  onClick={captureAndVerify} 
                  disabled={loading} 
                  className={`btn-mobile ${rostroVerificado ? 'btn-verified-state' : 'btn-verify-action'}`}
                >
                  {loading ? (
                    <>
                      <i className="fa-solid fa-circle-notch fa-spin"></i>
                      <span>Verificando...</span>
                    </>
                  ) : rostroVerificado ? (
                    <>
                      <i className="fa-solid fa-circle-check"></i>
                      <span>Rostro Validado (Toca para reintentar)</span>
                    </>
                  ) : (
                    <>
                      <i className="fa-solid fa-face-smile"></i>
                      <span>Escanear y Verificar Rostro</span>
                    </>
                  )}
                </button>
              )}
            </div>
          )}
        </div>

        {/* Selector de Tipo de Checada estilo Chips */}
        <div className="chips-section">
          <label className="section-subtitle">Tipo de Registro</label>
          <div className="chips-grid">
            {options.map((opt) => (
              <button
                key={opt.id}
                type="button"
                className={`chip-btn ${tipoChecada === opt.id ? 'selected' : ''}`}
                onClick={() => setTipoChecada(opt.id)}
              >
                <div className="chip-icon" style={{ color: opt.color }}>
                  <i className={`fa-solid ${opt.icon}`}></i>
                </div>
                <span className="chip-label">{opt.label}</span>
                {tipoChecada === opt.id && <div className="chip-dot" style={{ backgroundColor: opt.color }}></div>}
              </button>
            ))}
          </div>
        </div>

        {/* Botón Principal de Registrar Asistencia */}
        <div className="submit-section">
          <button
            onClick={handleCheckIn}
            disabled={loading || !rostroVerificado || !hasFaceRegistered}
            className={`btn-main-checkin ${rostroVerificado ? 'ready' : 'locked'}`}
          >
            {loading ? (
              <>
                <i className="fa-solid fa-circle-notch fa-spin"></i>
                <span>Registrando...</span>
              </>
            ) : !hasFaceRegistered ? (
              <>
                <i className="fa-solid fa-user-gear"></i>
                <span>Primero registra tu rostro</span>
              </>
            ) : !rostroVerificado ? (
              <>
                <i className="fa-solid fa-lock"></i>
                <span>Verifica tu rostro para checar</span>
              </>
            ) : (
              <>
                <i className="fa-solid fa-check-double"></i>
                <span>Confirmar y Registrar</span>
              </>
            )}
          </button>
        </div>

        {/* Mensajes y Notificaciones */}
        {message && (
          <div className={`mobile-toast ${message.type}`}>
            <div className="toast-icon">
              {message.type === 'success' && <i className="fa-solid fa-circle-check"></i>}
              {message.type === 'error' && <i className="fa-solid fa-circle-exclamation"></i>}
              {message.type === 'info' && <i className="fa-solid fa-circle-info"></i>}
            </div>
            <div className="toast-text">{message.text}</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CheckInComponent;